import get from 'lodash/get'
import * as React from 'react'
import { Redirect, Route, Router, Switch, withRouter } from 'react-router-dom'
import { StyleSheetManager } from 'styled-components'

import { AlertContext, Loading, namedSafeLazy, safeLazy } from '@avantstay/backoffice-core'

import Authorized from '@new/__global/components/Authorized'

import AuthProvider from '@contexts/Auth/AuthProvider'
import { setPostLoginRedirect } from '@contexts/Auth/methods/postLoginRedirect'
import StoreValuesProvider from '@contexts/StoreValuesContext/StoreValuesProvider'
import isPropValid from '@emotion/is-prop-valid'
import { ROUTES } from '@routes'
import GlobalStyle from '@styles/globalStyles'
import history from '@utils/history'
import { withErrorBoundary } from '@utils/withErrorBoundary'

import { MainScreensSwitch } from './MainScreensSwitch'

const Login = withErrorBoundary(safeLazy(() => import('@screens/Login/Login.screen')))
const ErrorAuthProcess = withErrorBoundary(safeLazy(() => import('@screens/ErrorAuth.screen')))
const OAuthConsentScreen = withErrorBoundary(
  safeLazy(() => import('@screens/Login/OAuthConsent.screen')),
)
const { FeatureToggleProvider } = namedSafeLazy(() => import('@utils/optimizely'))

const renderAuthorizing = () => {
  return <Route path={ROUTES.login.path} exact component={Login} />
}

const renderUnauthorized = withRouter(({ location }) => {
  const shouldRedirect = location.pathname !== ROUTES.login.path
  // Persist the intended destination so it survives Clerk's external OAuth
  // redirect (Google/Okta), which wipes React Router state. Skip the home path
  // so the existing "land on dashboard" default for signed-in users is kept.
  if (shouldRedirect && location.pathname !== ROUTES.home.path) {
    setPostLoginRedirect(location.pathname + location.search + location.hash)
  }
  return (
    <>
      <Route path={ROUTES.login.path} exact component={Login} />
      {shouldRedirect && (
        <Redirect
          to={{
            pathname: ROUTES.login.path,
            state: { nextLocation: location },
          }}
        />
      )}
    </>
  )
})

function MainRouter() {
  return (
    <StyleSheetManager
      shouldForwardProp={(propName, elementToBeRendered) => {
        return typeof elementToBeRendered === 'string' ? isPropValid(propName) : true
      }}
    >
      <Router history={history}>
        <AuthProvider>
          <FeatureToggleProvider>
            <StoreValuesProvider>
              <React.Suspense fallback={<Loading absolute />}>
                <Authorized
                  renderUnauthorized={renderUnauthorized}
                  renderAuthorizing={renderAuthorizing}
                  errorComponent={ErrorAuthProcess}
                >
                  {({ isSignedIn, location }) => {
                    // OAuth consent is a standalone, chrome-free page shown
                    // mid-OAuth-flow — render it without the app Layout/nav and
                    // bypass MainScreensSwitch's catch-all redirect.
                    if (location.pathname === ROUTES.oauthConsent.path) {
                      return <OAuthConsentScreen />
                    }
                    const shouldRedirectToDashboard =
                      isSignedIn &&
                      location.pathname === ROUTES.login.path &&
                      get(location.state, 'nextLocation.pathname') === ROUTES.home.path
                    return shouldRedirectToDashboard ? (
                      <Switch>
                        <Redirect to={ROUTES.dashboard.path} />
                      </Switch>
                    ) : (
                      <MainScreensSwitch />
                    )
                  }}
                </Authorized>
              </React.Suspense>
              <GlobalStyle />
              <AlertContext />
            </StoreValuesProvider>
          </FeatureToggleProvider>
        </AuthProvider>
      </Router>
    </StyleSheetManager>
  )
}

export default MainRouter
